import { useCallback, useEffect, useRef, useState } from 'react';
import { logError } from '@/lib/sentry';
import { fetchInventoryQuickAdd } from '@/supabase/inventory/service/inventoryService';
import { getAvailableStock } from '@/supabase/inventory/types/inventory';
import type { InventoryQuickAddItem } from '@/supabase/inventory/types/inventory';
import type { Item } from '@/types/purchase';

// RecentItemsSection 재고 뱃지 렌더 재료 — available(가용 = stock − reserved)은 서버 값에서
// 훅이 1회 파생해 담는다(state 미러가 아니라 fetch 시점 변환 — 원본 stock/reserved를 따로
// state로 들고 있지 않으므로 '미러 state 금지'와 충돌하지 않는다).
export interface QuickAddStockInfo {
  available: number;
  threshold: number;
}

export type QuickAddStockByNorm = ReadonlyMap<string, QuickAddStockInfo>;

export interface UseInventoryQuickAddResult {
  // null = "내 상품" 그룹 비노출(게스트 / 성공 로드 0회 / 활성 마스터 0개) — 소비처는 null이면
  // 기존 화면과 픽셀 동일하게 렌더한다(fail-closed, 화면 무변화 분기).
  masterItems: Item[] | null;
  stockByNorm: QuickAddStockByNorm | null;
  // 액션 후 재조회 진입점(stale-while-revalidate의 invalidation 조각) — 담기 계열 persist가
  // resolve된 "뒤에" 호출해야 read-your-writes가 보장된다. 게스트면 no-op. 서버 재파생이라
  // 어떤 경로로 예약이 바뀌었든 호출 한 번이면 정확(낙관 감산 금지 — 파생값 미러 장부가 됨).
  // 향후 realtime 증분도 이 진입점을 그대로 호출하면 된다.
  refreshQuickAdd: () => void;
}

interface QuickAddState {
  // 이 스냅샷이 속한 계정 — 반환부가 render-동기로 대조해 소유자가 다르면 null을 내린다
  // (codexloop R2 수용: effect 클리어는 커밋 이후에 돌아 userId 전환 직후 1프레임 동안
  // 이전 계정 데이터가 렌더되는 창이 있었음. 파생 가드는 그 프레임부터 막는다 — 미러 금지
  // 규율의 derive 패턴).
  ownerUserId: string;
  // 값-동등 비교용 원본(RPC items 그대로). masterItems/stockByNorm이 전부 여기서 파생되므로
  // 이 배열이 값-동등이면 파생물도 동등 — prev 참조를 그대로 반환해 memo 자식(ActiveCustomerColumn)
  // 의 불필요 재렌더를 막는다(탭 복귀 refresh마다 새 참조가 내려가면 memo가 장식이 됨).
  sourceItems: InventoryQuickAddItem[];
  masterItems: Item[];
  stockByNorm: QuickAddStockByNorm;
}

// 값-동등: 렌더 출력에 영향을 주는 필드 전부(id 포함 — id가 바뀌면 데이터가 바뀐 것).
function quickAddItemsEqual(a: InventoryQuickAddItem[], b: InventoryQuickAddItem[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.id !== y.id ||
      x.name !== y.name ||
      x.norm_name !== y.norm_name ||
      x.price !== y.price ||
      x.stock !== y.stock ||
      x.low_stock_threshold !== y.low_stock_threshold ||
      x.reserved !== y.reserved
    ) {
      return false;
    }
  }
  return true;
}

/**
 * /order '내 상품' 빠른 추가 도메인 훅(PR6). 재고 마스터(활성)를 Item[] 형태(클릭 = 기존
 * handleRecentItemAdd 경로 그대로)와 norm → {available, threshold} 맵(재고 뱃지)으로 내려준다.
 *
 * fail-closed 계약(플랜 §배선 1): 게스트(userId 없음)/성공 로드 0회/마스터 0개 = 둘 다 null →
 * 소비처 화면 무변화. 실패 시 state 무접촉(성공 시만 교체) — 성공 이력이 있으면 이전 값 보존
 * (파생 예약이라 stale해도 틀리지 않고 늦을 뿐, 플랜 §배선 4). 토스트 없음 — /order 주문 플로우에
 * 재고 부가 기능이 소음을 얹으면 안 된다(logError만).
 *
 * 갱신 주기 = mount + visibilitychange(visible 복귀, useExcelKeepInfo 트리거 대칭) +
 * **액션 후 재조회(refreshQuickAdd — 오너 지시 2026-07-12 "실시간 반영 필요")**: Home이 담기
 * 계열 persist가 resolve된 뒤 호출해 클릭 즉시 뱃지가 움직인다. mountedRef + seq(latest-wins)
 * 가드는 useInventory와 동일 계열.
 *
 * userId 전환은 방향 무관(로그아웃·계정 A→B) 즉시 null 수렴 — 2중 방어(codexloop R1 P1 +
 * R2 후속 수용): ① 반환부의 ownerUserId render-동기 대조가 전환 "그 프레임"부터 이전 계정
 * 스냅샷을 null로 강등(effect 클리어는 커밋 이후라 이것만으로는 1프레임 창이 남았음),
 * ② effect 진입 시 setData(null)은 이전 스냅샷 참조 해제용. 첫 마운트에서는 state가 이미
 * null이라 no-op(React 동일값 bail-out — 렌더 비용 0).
 */
export function useInventoryQuickAdd(userId: string | undefined): UseInventoryQuickAddResult {
  const [data, setData] = useState<QuickAddState | null>(null);
  // latest-wins seq — mount fetch·visibilitychange·액션 후 재조회가 전부 공유(역전 응답 폐기).
  // effect-로컬이던 것을 훅 레벨로 승격: refreshQuickAdd를 밖으로 노출해야 해서(액션 후 재조회),
  // 소유자 전환 시엔 effect 진입부의 seq 증가가 이전 소유자 in-flight까지 전부 무효화한다.
  const requestSeqRef = useRef(0);
  const mountedRef = useRef(true);

  // 언마운트 가드 — setup에서 true 리셋(StrictMode setup→cleanup→setup 재마운트에 가드가
  // 영구 false로 굳지 않도록, useInventory C1 학습과 동일).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refreshQuickAdd = useCallback(() => {
    // uid를 호출 시점에 캡처 — 응답 도착 시 setData의 ownerUserId는 "요청을 발행한 소유자"다.
    // (그 사이 소유자가 바뀌었으면 effect 진입부 seq 증가가 이 응답을 폐기한다.)
    const uid = userId;
    if (!uid) return;
    const seq = ++requestSeqRef.current;
    fetchInventoryQuickAdd()
      .then(result => {
        if (!mountedRef.current || seq !== requestSeqRef.current) return;
        if (result.items.length === 0) {
          // 성공 응답의 "마스터 0개"는 실패가 아니라 새 진실(전부 삭제 등) — null로 수렴해
          // 그룹을 내린다. 실패-보존과 다른 분기임에 주의.
          setData(null);
          return;
        }
        setData(prev => {
          // prev 재사용은 같은 소유자일 때만 — 소유자가 다르면 값이 우연히 같아도 새
          // 스냅샷으로 교체해야 반환부의 ownerUserId 대조가 성립한다.
          if (prev && prev.ownerUserId === uid && quickAddItemsEqual(prev.sourceItems, result.items)) {
            return prev;
          }
          const masterItems: Item[] = result.items.map(it => ({
            name: it.name,
            price: it.price,
            quantity: 1,
          }));
          const stockByNorm = new Map<string, QuickAddStockInfo>(
            result.items.map(it => [
              it.norm_name,
              { available: getAvailableStock(it), threshold: it.low_stock_threshold },
            ]),
          );
          return { ownerUserId: uid, sourceItems: result.items, masterItems, stockByNorm };
        });
      })
      .catch(err => {
        if (!mountedRef.current || seq !== requestSeqRef.current) return;
        logError('useInventoryQuickAdd', err);
      });
  }, [userId]);

  useEffect(() => {
    // 소유자 전환(로그아웃 포함) 즉시: 이전 스냅샷 참조 해제 + 진행 중 요청 전부 무효화.
    // (로그아웃 경로는 아래 early-return이라 seq 증가를 여기서 해야 이전 in-flight가 죽는다.)
    requestSeqRef.current++;
    setData(null);
    if (!userId) {
      return;
    }
    refreshQuickAdd();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshQuickAdd();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, refreshQuickAdd]);

  // render-동기 소유자 대조: effect(커밋 후) 클리어만으로는 userId 전환 직후 1프레임 동안
  // 이전 계정 스냅샷이 그대로 반환되는 창이 남는다 — 여기서 소유자가 다르면 그 프레임부터
  // null(화면 무변화)로 강등한다. effect의 setData(null)은 참조 해제(메모리)용으로 유지.
  const scoped = data && data.ownerUserId === userId ? data : null;
  return {
    masterItems: scoped?.masterItems ?? null,
    stockByNorm: scoped?.stockByNorm ?? null,
    refreshQuickAdd,
  };
}
